import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Link, SvgIcon, Tooltip, Typography } from "@mui/material";
import { ArrowPopover } from "@verity/components/core";
import LightBulbIcon from "../../../images/portal/light-bulb.svg?react";
import VideoIcon from "../../../images/portal/video.svg?react";
import PopoutIcon from "../../../images/portal-navigation/popoutIcon.svg?react";
import styles from "./index.module.css";

type VideoPopoverProps = {
	src: string;
	videoTitle?: string;
	linkText: string;
	header?: ReactNode;
	description?: ReactNode;
};

export default function VideoPopover({ src, videoTitle, linkText, header, description }: VideoPopoverProps) {
	const { i18n } = useTranslation();
	const [open, setOpen] = useState(false);
	const linkRef = useRef<HTMLDivElement>(null);

	return (
		<div className={styles.videoLink}>
			<div className={styles.videoLinkText}>
				<LightBulbIcon />
				<Typography>
					<strong>{i18n.t("portal.components.videoPopoverTSX.howTo")}</strong>
					{linkText}
				</Typography>
			</div>
			<div ref={linkRef} className={styles.videoPopoverLinkContainer}>
				<Link
					component="button"
					aria-haspopup="dialog"
					aria-expanded={open}
					onClick={() => setOpen(true)}
					className={styles.videoPopoverLink}
					color="#0B6F85"
				>
					<VideoIcon />
					{i18n.t("portal.components.videoPopoverTSX.watchVideo")}
				</Link>
			</div>
			<ArrowPopover
				open={open}
				onClose={() => setOpen(false)}
				anchorEl={linkRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				slotProps={{
					paper: {
						sx: {
							width: "100%",
							maxWidth: { xs: "calc(100vw - 64px)", sm: "450px" },
							overflow: "visible",
							border: "1.5px solid #e4e4e4",
							borderRadius: "7px",
							marginTop: 1.5,
						},
					},
				}}
			>
				{(header || description) && (
					<div className={styles.popoverHeader}>
						{header && (
							<div className={styles.header}>
								<Typography fontSize="15px" fontWeight="500">
									{header}
								</Typography>
							</div>
						)}
						{description && (
							<div className={styles.description}>
								<Typography fontSize="12px" fontWeight="400">
									{description}
								</Typography>
								<Tooltip title={i18n.t("portal.components.videoPopoverTSX.popoutButtonAriaLabel")}>
									<IconButton
										component="a"
										href={src}
										target="_blank"
										rel="noopener noreferrer"
										disableRipple
										sx={{
											padding: 0,
											minWidth: 0,
											"&:focus-visible": {
												outline: "2px solid var(--focus-outline-color-dark)",
												borderRadius: "2px",
											},
										}}
									>
										<SvgIcon>
											<PopoutIcon />
										</SvgIcon>
									</IconButton>
								</Tooltip>
							</div>
						)}
					</div>
				)}
				<iframe src={src} title={videoTitle} className={styles.video} allowFullScreen loading="lazy" />
			</ArrowPopover>
		</div>
	);
}
